import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthContextProvider } from "./auth/Auth";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { FallbackComponent } from "./feil/FallbackComponent";
import AnmeldelseRouter from "./routing/AnmeldelseRouter";
import { BannerComponent } from "./BannerComponent";
import LoginBar from "./auth/LoginBar";
import { mockPnoCore } from "./setup";
import { Languages, toLanguage } from "./i18n/config";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error("QueryCache error", query, error);
    },
  }),
});

interface PnoProps {
  lang: Languages;
}

const Header = ({ lang, disableLoginBar = false }: PnoProps & {disableLoginBar?: boolean}) => {
  const { t } = useTranslation();
  return (
    <div id={"headerId"}>
      <aside className="navigate-to-content">
        <a href="#content">{t("navigerTilInnhold")}</a>
      </aside>
      {mockPnoCore ? (
        <header className="mockPno mockHeader">
          PLACEHOLDER POLITIET.NO HEADER
        </header>
      ) : (
        <>
          {!disableLoginBar && <LoginBar lang={lang} />}
          <pno-header lang={lang} isLoginBarHidden></pno-header>
        </>
      )}
      <BannerComponent />
    </div>
  );
};

const Footer = ({ lang }: PnoProps) => {
  return mockPnoCore ? (
    <footer className="mockPno mockFooter">
      PLACEHOLDER POLITIET.NO FOOTER
    </footer>
  ) : (
    <pno-footer id={"footerId"} lang={lang}></pno-footer>
  );
};

function App() {
  const { i18n } = useTranslation();
  useEffect(() => {
    i18n.on("languageChanged", (lng) => (document.documentElement.lang = lng));
  }, [i18n]);

  //Header og footer har dårlig støtte for nynorsk (og engelsk), derfor tenker vi det er bedre å bruke nb i stedet for nn ihvertfall
  const pnoLanguage = toLanguage(
    i18n.language === "nn" ? "nb" : i18n.language,
  );

  const headerComponent = <Header lang={pnoLanguage} />;

  return (
    // .pno-app_body klassen forventer å finne 3 elementer under seg.
    // Hvis den får flere elementer så vil grid layouten se rar ut
    <div className="App pno-app__body">
      <ErrorBoundary FallbackComponent={FallbackComponent}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary FallbackComponent={FallBackComponentWithHeader}>
            <AuthContextProvider headerComponent={headerComponent}>
              {headerComponent}
              <ErrorBoundary FallbackComponent={FallbackComponent}>
                <AnmeldelseRouter />
              </ErrorBoundary>
            </AuthContextProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </ErrorBoundary>
      <Footer lang={pnoLanguage} />
    </div>
  );
}

function FallBackComponentWithHeader(props: FallbackProps) {
  return (
    <>
      <Header lang={Languages.nb} disableLoginBar />
      <FallbackComponent {...props} />
    </>
  );
}

export default App;
